// Universal API fetch wrapper - centralizes all API calls with proper CORS handling
import { buildApiUrl, getApiHeaders } from '@/config/api'

export interface ApiFetchOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>
  includeAuth?: boolean
}

/**
 * Universal API fetch function that ensures:
 * - Proper /dev prefix for API Gateway  
 * - NO trailing slashes to prevent 307 redirects that strip CORS headers
 * - Correct CORS headers and referrer policy
 * - Centralized error handling
 */
export async function apiFetch(endpoint: string, options: ApiFetchOptions = {}) {
  const { headers = {}, includeAuth = true, ...fetchOptions } = options
  
  // Build URL using centralized configuration (includes /dev prefix, NO trailing slash)
  const url = buildApiUrl(endpoint)
  
  const config: RequestInit = {
    headers: {
      ...getApiHeaders(includeAuth),
      ...headers,
    },
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'omit',
    ...fetchOptions,
  }

  console.log('🌐 API Fetch:', url)
  
  try {
    const response = await fetch(url, config)
    return response
  } catch (error) {
    console.error('API Fetch Error:', error)
    throw error
  }
}

/**
 * Universal API request function that handles JSON responses
 */
export async function apiRequest<T = any>(endpoint: string, options: ApiFetchOptions = {}): Promise<{
  success: boolean
  data?: T
  error?: string
  status: number
}> {
  try {
    const response = await apiFetch(endpoint, options)
    const data = await response.json()
    
    return {
      success: response.ok,
      data: response.ok ? data : undefined,
      error: !response.ok ? (data.message || data.error || `HTTP ${response.status}`) : undefined,
      status: response.status
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
      status: 0
    }
  }
}

/**
 * Authenticated API fetch - alias for apiFetch with auth enabled by default
 */
export async function fetchWithAuth(endpoint: string, options: Omit<ApiFetchOptions, 'includeAuth'> = {}) {
  return apiFetch(endpoint, { ...options, includeAuth: true })
}
